<script setup lang="ts">
import { inject, onBeforeMount, Ref, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import useVuelidate from '@vuelidate/core';
import { helpers, required } from '@vuelidate/validators';
import { STATUS_CLOSED, STATUS_OPEN } from '@/constants';
import { isEmptyValue } from '@/utils/helper';
import { useAirManagementStore } from '@/store/AirManagementStore';
import { useToast } from 'primevue/usetoast';
import AutoComplete, { AutoCompleteCompleteEvent, AutoCompleteOptionSelectEvent } from 'primevue/autocomplete';
import Button from 'primevue/button';
import Fluid from 'primevue/fluid';
import InputText from 'primevue/inputtext';
import ToggleSwitch from 'primevue/toggleswitch';
import LoadingSpinner from '@/components/LoadingSpinner.vue';

export type TAirport = {
  airportCode: string;
  airportName: string;
  region: string;
  country: string;
  gateSts: string;
  gatewayName: string;
  airportNameFR?: string;
  cityName?: string;
  cityNameFr?: string;
  regionFr?: string;
  countryName?: string;
  countryNameFr?: string;
};

const dialogRef = inject('dialogRef') as Ref;
const gatewayData = dialogRef?.value?.data?.gateway;
const onSubmit = dialogRef?.value?.options?.emits?.onSubmit;

const { t } = useI18n({ useScope: 'global' });
const toast = useToast();
const AirManagementStore = useAirManagementStore();

const defaultGateway = {
  pk: null,
  sk: null,
  code: '',
  name: '',
  nameFr: '',
  airportName: '',
  airportNameFR: '',
  region: '',
  regionFr: '',
  province: null,
  countryName: '',
  countryNameFr: '',
  destinations: null,
  destDurations: {},
  status: STATUS_CLOSED,
  gatewayType: 'MANUAL',
  airportDestSVId: ''
};

const initData = () => {
  const combinedData = isEmptyValue(gatewayData) ? gatewayData : defaultGateway;
  return {
    ...combinedData,
    airportDestSVId: ''
  };
};

const airportSelected = ref(initData());

const airports = ref<TAirport[]>([]);
const filterAirport = ref<TAirport[]>([]);
const isLoading = ref(false);

const rules = {
  code: {
    required: helpers.withMessage(t('validate.required'), required)
  },
  airportName: {
    required: helpers.withMessage(t('validate.required'), required)
  },
  airportNameFR: {
    required: helpers.withMessage(t('validate.required'), required)
  },
  name: {
    required: helpers.withMessage(t('validate.required'), required)
  },
  nameFr: {
    required: helpers.withMessage(t('validate.required'), required)
  },
  region: {
    required: helpers.withMessage(t('validate.required'), required)
  },
  regionFr: {
    required: helpers.withMessage(t('validate.required'), required)
  },
  countryName: {
    required: helpers.withMessage(t('validate.required'), required)
  },
  countryNameFr: {
    required: helpers.withMessage(t('validate.required'), required)
  }
};

const v$ = useVuelidate(rules, airportSelected);

const searchAirportCode = (event: AutoCompleteCompleteEvent) => {
  if (!event.query.trim().length) {
    filterAirport.value = [...airports.value.slice(0, 100)];
  } else {
    filterAirport.value = airports.value.filter((airport) => {
      return airport.airportCode?.toLowerCase().startsWith(event.query.toLowerCase());
    });
  }
};

const getListAirports = async () => {
  if (!gatewayData?.airportCode) {
    isLoading.value = true;
    await AirManagementStore.getListAir()
      .then((response) => {
        airports.value = response?.map((item: any) => {
          const { cityName } = item;
          return {
            ...item,
            gatewayName: cityName
          };
        });
      })
      .finally(() => {
        isLoading.value = false;
      });
  }
};

const onAddGateWay = async () => {
  v$.value.$validate();
  if (!v$.value.$errors.length) {
    try {
      await onSubmit(airportSelected.value);
      toast.add({ severity: 'success', summary: '', detail: t('notification.save_successfully'), life: 3000 });
      dialogRef.value.close();
    } catch (error) {
      toast.add({ severity: 'error', summary: '', detail: t('notification.save_error'), life: 5000 });
    }
  }
};

const onChangeGateway = (event: AutoCompleteOptionSelectEvent) => {
  const selectedAir = airports.value.find((item) => item.airportCode === event.value.airportCode);
  if (selectedAir) {
    airportSelected.value = {
      ...initData(),
      code: selectedAir.airportCode ?? '',
      airportName: selectedAir.airportName,
      airportNameFR: selectedAir.airportNameFR,
      name: selectedAir.cityName,
      nameFr: selectedAir.cityNameFr,
      region: selectedAir.region,
      regionFr: selectedAir.regionFr,
      countryName: selectedAir.countryName,
      countryNameFr: selectedAir.countryNameFr,
      airportDestSVId: selectedAir.destSVId ?? ''
    };
  }
};

const onUnselectGateway = () => {
  airportSelected.value = initData();
};

onBeforeMount(() => {
  getListAirports();
});
</script>
<template>
  <LoadingSpinner v-if="isLoading" />
  <div>
    <Fluid>
      <label for="hotelRoomCate" class="mb-1 inline-block">{{ `${$t('management.air.airport_code')}` }}</label>
      <div class="flex align-content-start gap-3">
        <div class="field flex-1">
          <div>
            <AutoComplete
              :disabled="!!gatewayData?.code"
              v-model="airportSelected.code"
              @item-select="onChangeGateway"
              @clear="onUnselectGateway"
              :suggestions="filterAirport"
              @complete="searchAirportCode($event)"
              completeOnFocus
              class="w-full"
              :class="v$.code.$error ? 'p-invalid' : ''"
              forceSelection
              :optionLabel="'airportCode'"
              scrollHeight="168px"
              id="airport_code"
            >
              <template #option="slotProps">
                <div class="flex align-options-center autocomplete-label">
                  <div>
                    <div>{{ slotProps?.option?.airportName }}</div>
                    <div class="text-xs">{{ slotProps?.option?.cityName }}, {{ slotProps?.option?.region || slotProps?.option?.province }}, {{ slotProps?.option?.countryName }} ({{ slotProps?.option?.airportCode }})</div>
                  </div>
                </div>
              </template>
            </AutoComplete>
            <p class="text-red-500 pt-1" v-if="v$.code.$error">{{ $t('validate.required') }}</p>
          </div>
        </div>
        <div class="flex-1"></div>
      </div>
    </Fluid>
    <Fluid class="mt-2">
      <div class="flex align-content-start gap-3">
        <div class="field flex-1">
          <label for="airport_name">{{ $t('management.package.airport_name_en') }}</label>
          <InputText :class="v$.airportName.$error && !!airportSelected.code ? 'p-invalid' : ''" :disabled="!airportSelected.code" v-model="airportSelected.airportName" id="airport_name" />
          <p class="text-red-500 pt-1" v-if="v$.airportName.$error && !!airportSelected.code">{{ $t('validate.required') }}</p>
        </div>
        <div class="field flex-1">
          <label for="airport_name_fr">{{ $t('management.package.airport_name_fr') }}</label>
          <InputText :class="v$.airportNameFR.$error && !!airportSelected.code ? 'p-invalid' : ''" :disabled="!airportSelected.code" v-model="airportSelected.airportNameFR" id="airport_name_fr" />
          <p class="text-red-500 pt-1" v-if="v$.airportNameFR.$error && !!airportSelected.code">{{ $t('validate.required') }}</p>
        </div>
      </div>

      <div class="flex align-content-start gap-3">
        <div class="field flex-1">
          <label for="gateway_name">{{ $t('management.package.gateway_name_en') }}</label>
          <InputText :class="v$.name.$error && !!airportSelected.code ? 'p-invalid' : ''" :disabled="!airportSelected.code" v-model="airportSelected.name" id="gateway_name" />
          <p class="text-red-500 pt-1" v-if="v$.name.$error && !!airportSelected.code">{{ $t('validate.required') }}</p>
        </div>
        <div class="field flex-1">
          <label for="gateway_name_fr">{{ $t('management.package.gateway_name_fr') }}</label>
          <InputText :class="v$.nameFr.$error && !!airportSelected.code ? 'p-invalid' : ''" :disabled="!airportSelected.code" v-model="airportSelected.nameFr" id="gateway_name_fr" />
          <p class="text-red-500 pt-1" v-if="v$.nameFr.$error && !!airportSelected.code">{{ $t('validate.required') }}</p>
        </div>
      </div>

      <div class="flex align-content-start gap-3">
        <div class="field flex-1">
          <label for="region">{{ $t('management.package.region_en') }}</label>
          <InputText :class="v$.region.$error && !!airportSelected.code ? 'p-invalid' : ''" :disabled="!airportSelected.code" v-model="airportSelected.region" id="region" />
          <p class="text-red-500 pt-1" v-if="v$.region.$error && !!airportSelected.code">{{ $t('validate.required') }}</p>
        </div>
        <div class="field flex-1">
          <label for="region_fr">{{ $t('management.package.region_fr') }}</label>
          <InputText :class="v$.regionFr.$error && !!airportSelected.code ? 'p-invalid' : ''" :disabled="!airportSelected.code" v-model="airportSelected.regionFr" id="region_fr" />
          <p class="text-red-500 pt-1" v-if="v$.regionFr.$error && !!airportSelected.code">{{ $t('validate.required') }}</p>
        </div>
      </div>

      <div class="flex align-content-start gap-3">
        <div class="field flex-1">
          <label for="country">{{ $t('management.package.country_name_en') }}</label>
          <InputText :class="v$.countryName.$error && !!airportSelected.code ? 'p-invalid' : ''" :disabled="!airportSelected.code" v-model="airportSelected.countryName" id="country" />
          <p class="text-red-500 pt-1" v-if="v$.countryName.$error && !!airportSelected.code">{{ $t('validate.required') }}</p>
        </div>
        <div class="field flex-1">
          <label for="country_fr">{{ $t('management.package.country_name_fr') }}</label>
          <InputText :class="v$.countryNameFr.$error && !!airportSelected.code ? 'p-invalid' : ''" :disabled="!airportSelected.code" v-model="airportSelected.countryNameFr" id="country_fr" />
          <p class="text-red-500 pt-1" v-if="v$.countryNameFr.$error && !!airportSelected.code">{{ $t('validate.required') }}</p>
        </div>
      </div>

      <div class="flex align-content-start gap-3">
        <div class="field">
          <label for="gateway_status">{{ $t('management.air.gateway_status') }}</label>
          <div>
            <ToggleSwitch v-model="airportSelected.status" :trueValue="STATUS_OPEN" :falseValue="STATUS_CLOSED" id="gateway_status" />
          </div>
        </div>
      </div>
    </Fluid>
    <div class="w-full flex justify-content-center">
      <Button data-testid="add_gateway" class="font-bold" @click="onAddGateWay()">{{ $t('common.add') }}</Button>
    </div>
  </div>
</template>
